import CoreApi, { type GamificationTransaction } from '../../api/CoreApi';
import { meGetGamificationAccountTransactions } from '../../features/queries/meGetGamificationAccountTransactions';
import { meGetGamificationAccounts } from '../../features/queries/meGetGamificationAccounts';
import { meGetGamificationTransaction } from '../../features/queries/meGetGamificationTransaction';
import { useM, useQ } from '../../lib/query';
import {
  useGamification,
  type InfiniteTransactionsData,
} from '../../providers/GamificationProvider';
import { useLogger } from '../../providers/LoggerProvider';
import { __, formatPoints, toastSuccess, toastError } from '../../utils';
import { Link } from '../Link';
import { TRANSACTIONS_LIST_LIMIT } from './TransactionsList';
import {
  Box,
  Button,
  Modal,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { sk } from 'date-fns/locale';
import React from 'react';

type Props = {
  openTransactionId?: number;
  accountId?: number;
  setOpenTransactionId: (id: number | null) => void;
  onClose?: () => void;
};

const TransactionsDetail: React.FC<Props> = ({
  openTransactionId,
  accountId,
  setOpenTransactionId,
  onClose,
}) => {
  const [remainingTime, setRemainingTime] = React.useState<string>('');
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const { logger } = useLogger();
  const { updateGeneralAccountPoints } = useGamification();
  const queryClient = useQueryClient();

  const { queryKey: transactionsQueryKey } = useQ(
    meGetGamificationAccountTransactions,
    { accountId: accountId, limit: TRANSACTIONS_LIST_LIMIT },
    {
      enabled: false,
    },
  );

  const { queryKey: meGetGamificationAccountsQueryKey } = useQ(
    meGetGamificationAccounts,
    undefined,
    {
      enabled: false,
    },
  );

  const {
    data: detailTransaction,
    isLoading: isLoadingDetailTransaction,
    queryKey: detailTransactionQueryKey,
  } = useQ(
    meGetGamificationTransaction,
    { transactionId: openTransactionId },
    { enabled: !!openTransactionId },
  );

  const closeModal = () => {
    setOpenTransactionId(null);
    if (onClose) {
      onClose();
    }
  };

  const confirmTransactionMutation = useM(
    (transactionId: number) =>
      CoreApi.meConfirmGamificationTransaction(transactionId),
    {
      onSuccess: async () => {
        toastSuccess(__('Transakcia bola úspešne potvrdená'));

        // Update transaction status in the transactions list
        queryClient.setQueryData(
          transactionsQueryKey,
          (oldData: InfiniteTransactionsData) => {
            if (!oldData || !oldData.pages) return oldData;

            const updatedPages = oldData.pages.map((page) => ({
              ...page,
              transactions: page.transactions.map((tx) =>
                tx.transaction_id === openTransactionId
                  ? {
                      ...tx,
                      status: 'confirmed',
                    }
                  : tx,
              ),
            }));

            return {
              ...oldData,
              pages: updatedPages,
            };
          },
        );

        // Update the detail transaction status
        queryClient.setQueryData(
          detailTransactionQueryKey,
          (oldDetail: GamificationTransaction) => ({
            ...oldDetail,
            status: 'confirmed',
          }),
        );

        // Close the modal
        closeModal();
      },
      onError: (error) => {
        toastError(__('Nepodarilo sa potvrdiť transakciu'));

        logger.error('Error confirming gamification transaction', {
          action: 'confirmTransaction',
          error,
          data: {
            transactionId: openTransactionId,
            accountId,
          },
        });
      },
    },
  );

  React.useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (
      detailTransaction?.status === 'reserved' &&
      detailTransaction?.expires_at
    ) {
      const expiresAt = new Date(detailTransaction.expires_at).getTime() + 2000;

      const updateTimer = () => {
        const now = new Date().getTime();
        const timeDiff = expiresAt - now;

        if (timeDiff > 0) {
          const duration = format(
            timeDiff,
            timeDiff >= 3600000 ? "H'h' m'm' s's'" : "m'm' s's'",
          );
          setRemainingTime(duration);
        } else {
          setRemainingTime('0s');

          // Clear timers
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          if (accountId) {
            const originalPoints = detailTransaction.points || 0;
            updateGeneralAccountPoints(
              accountId,
              // return transactions points
              originalPoints < 0 ? -1 * originalPoints : originalPoints,
            );
          }

          queryClient.setQueryData(
            transactionsQueryKey,
            (oldData: InfiniteTransactionsData) => {
              // Typový guard pre infinite query data
              if (
                !oldData ||
                typeof oldData !== 'object' ||
                !('pages' in oldData)
              )
                return oldData;

              if (!oldData.pages || oldData.pages.length === 0) return oldData;

              const updatedPages = oldData.pages.map((page) => ({
                ...page,
                transactions: page.transactions.map((tx) =>
                  tx.transaction_id === detailTransaction.transaction_id
                    ? {
                        ...tx,
                        status: 'expired',
                        points: 0,
                        original_points: detailTransaction.points,
                      }
                    : tx,
                ),
              }));

              queryClient.setQueryData(
                detailTransactionQueryKey,
                (oldDetail: GamificationTransaction) => ({
                  ...oldDetail,
                  status: 'expired',
                  points: 0,
                  original_points: detailTransaction.points,
                }),
              );

              return {
                ...oldData,
                pages: updatedPages,
              };
            },
          );

          closeModal();
        }
      };

      updateTimer();
      intervalRef.current = setInterval(updateTimer, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    detailTransaction,
    queryClient,
    transactionsQueryKey,
    meGetGamificationAccountsQueryKey,
    closeModal,
    accountId,
    updateGeneralAccountPoints,
    detailTransactionQueryKey,
  ]);

  const getPointsStyle = () => {
    if (!detailTransaction) return {};

    const isNegative = (detailTransaction.points || 0) < 0;

    switch (detailTransaction.status) {
      case 'expired':
      case 'cancelled':
        return {
          color: 'text.disabled',
        };
      case 'reserved':
        return {
          color: 'warning.main',
        };
      case 'confirmed':
        return {
          color: isNegative ? 'error.main' : 'success.main',
        };
      default:
        return {};
    }
  };

  const getStatusText = () => {
    if (!detailTransaction) return '';

    switch (detailTransaction.status) {
      case 'expired':
        return `${__('Expirované')} ${format(new Date(detailTransaction.expires_at || detailTransaction.created_at), 'dd.MM.yyyy, HH:mm', { locale: sk })}`;
      case 'reserved':
        return (
          <>
            {__('Rezervácia ešte bude trvať ')}
            <b>{remainingTime}</b>
          </>
        );
      case 'confirmed':
        return format(
          new Date(detailTransaction.created_at),
          'dd.MM.yyyy, HH:mm',
          { locale: sk },
        );
      default:
        return '';
    }
  };

  const formatPointsWithSign = (points: number) => {
    const formattedPoints = formatPoints(Math.abs(points));
    const sign = points > 0 ? '+' : points < 0 ? '-' : '';
    return `${sign}${formattedPoints} b`;
  };

  const getModalTitle = () => {
    if (!detailTransaction) return __('Detail Transakcie');

    switch (detailTransaction.status) {
      case 'reserved':
        return __('Rezervácia bodov');
      case 'expired':
        return __('Expirovaná transakcia');
      case 'confirmed':
      default:
        return __('Detail Transakcie');
    }
  };

  const renderButtons = () => {
    if (detailTransaction?.status === 'reserved') {
      return (
        <Stack
          direction='column'
          spacing={1}
          justifyContent='center'
          width={'100%'}
        >
          <Button
            variant='contained'
            color='primary'
            loading={confirmTransactionMutation.isPending}
            onClick={async () => {
              if (detailTransaction?.transaction_id) {
                try {
                  await confirmTransactionMutation.mutateAsync(
                    detailTransaction.transaction_id,
                  );
                } catch {
                  // Empty catch because mutation handles errors
                }
              }
            }}
          >
            {__('Potvrdiť')}
          </Button>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => closeModal()}
            fullWidth
          >
            {__('Zavrieť')}
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction={'row'}>
        <Button
          fullWidth
          variant='outlined'
          onClick={() => closeModal()}
          sx={{ alignSelf: 'center', marginTop: 2 }}
        >
          {__('Zavrieť')}
        </Button>
      </Stack>
    );
  };

  if (!openTransactionId) {
    return null;
  }

  return (
    <Modal open={!!openTransactionId} onClose={() => closeModal()}>
      <Box
        component='div'
        sx={{
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxHeight: '100vh',
          maxWidth: '100vw',
          boxShadow: 24,
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 2,
          minWidth: 400,
        }}
      >
        {/* Header with title and status text */}
        <Stack
          direction='column'
          justifyContent='center'
          alignItems='center'
          mb={3}
          spacing={1}
        >
          <Typography variant='h6'>{getModalTitle()} </Typography>
          {isLoadingDetailTransaction ? (
            <Skeleton variant='text' width={120} />
          ) : (
            <Typography
              lineHeight={1}
              variant='caption'
              textAlign='center'
              color={
                detailTransaction?.status === 'expired' ||
                detailTransaction?.status === 'cancelled'
                  ? 'text.disabled'
                  : detailTransaction?.status === 'reserved'
                    ? 'warning.main'
                    : 'text.secondary'
              }
            >
              {`ID: ${detailTransaction.transaction_id}`}
            </Typography>
          )}
          {isLoadingDetailTransaction ? (
            <Skeleton variant='text' width={120} />
          ) : (
            getStatusText() && (
              <Typography
                lineHeight={1}
                variant='caption'
                color={
                  detailTransaction?.status === 'expired' ||
                  detailTransaction?.status === 'cancelled'
                    ? 'text.disabled'
                    : detailTransaction?.status === 'reserved'
                      ? 'warning.main'
                      : 'text.secondary'
                }
                textAlign='center'
              >
                {getStatusText()}
              </Typography>
            )
          )}
        </Stack>

        {/* Points in center */}
        <Stack alignItems='center' spacing={1} mb={3}>
          {isLoadingDetailTransaction ? (
            <Skeleton variant='text' width={100} height={60} />
          ) : (
            <Typography
              variant='body1'
              fontWeight='bold'
              fontSize={48}
              sx={getPointsStyle()}
            >
              {formatPointsWithSign(
                detailTransaction?.status === 'expired'
                  ? detailTransaction?.original_points || 0
                  : detailTransaction?.points || 0,
              )}
            </Typography>
          )}
        </Stack>

        {/* Description */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent={'center'}
          mb={4}
          gap={0.5}
        >
          {isLoadingDetailTransaction ? (
            <Skeleton variant='text' width={200} />
          ) : (
            <>
              <Typography
                variant='body2'
                lineHeight={1}
                textAlign='center'
                color='text.primary'
              >
                {detailTransaction?.description || '-'}
              </Typography>
              {(detailTransaction?.payload as { url?: string })?.url && (
                <Link
                  sx={{
                    gap: 0.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: ({ palette }) => palette.info.main,
                  }}
                  target='_blank'
                  to={(detailTransaction.payload as { url: string }).url}
                >
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              )}
            </>
          )}
        </Stack>

        {/* Buttons */}
        {!isLoadingDetailTransaction && renderButtons()}
      </Box>
    </Modal>
  );
};

export default TransactionsDetail;
